// Client-side climate data (mirror of backend climate_data.py).
// Used for instant in-browser calculations & department -> zone resolution.

export const TEMPERATURE_TABLE = {
  A: { 200: -2, 400: -4, 600: -5, 800: -7, 1000: -8, 1200: -9 },
  B: { 200: -4, 400: -5, 600: -6, 800: -8, 1000: -9, 1200: -10 },
  C: { 200: -5, 400: -6, 600: -7, 800: -9, 1000: -11, 1200: -12 },
  D: { 200: -7, 400: -8, 600: -9, 800: -11, 1000: -13, 1200: -14 },
  E: { 200: -8, 400: -9, 600: -10, 800: -12, 1000: -14, 1200: -16 },
  F: { 200: -9, 400: -10, 600: -11, 800: -13, 1000: -15, 1200: -17 },
  G: { 200: -10, 400: -11, 600: -12, 800: -14, 1000: -17, 1200: -19 },
  H: { 200: -12, 400: -13, 600: -14, 800: -17, 1000: -19, 1200: -21 },
  I: { 200: -15, 400: -15, 600: -17, 800: -19, 1000: -21, 1200: -23 },
};

export const ZONE_COLORS = {
  A: "#312e81",
  B: "#4338ca",
  C: "#3b82f6",
  D: "#0ea5e9",
  E: "#0d9488",
  F: "#10b981",
  G: "#f59e0b",
  H: "#ea580c",
  I: "#e11d48",
};

export const ALTITUDE_BANDS = [200, 400, 600, 800, 1000, 1200];

export const ALTITUDE_OPTIONS = [
  { value: 200, label: "0 - 200 m" },
  { value: 400, label: "201 - 400 m" },
  { value: 600, label: "401 - 600 m" },
  { value: 800, label: "601 - 800 m" },
  { value: 1000, label: "801 - 1000 m" },
  { value: 1200, label: "1001 - 1200 m" },
];

export const ISOLATION_COEFFICIENTS = {
  RT2020: { label: "RT 2020 (BBC / RE2020)", value: 0.4 },
  RT2012: { label: "RT 2012", value: 0.6 },
  RT2005: { label: "RT 2005", value: 0.8 },
  RT2000: { label: "RT 2000", value: 0.9 },
  bonne_isolation: { label: "Bonne isolation", value: 1.1 },
  moyenne_isolation: { label: "Moyenne isolation", value: 1.3 },
  non_isole: { label: "Non isolé", value: 1.6 },
};

// Legacy isolation keys (rétro-compatibilité des projets sauvegardés).
export const LEGACY_ISOLATION = {
  annees_90: "bonne_isolation",
  annees_80: "moyenne_isolation",
};

export const EMETTEURS = {
  basse_temp: {
    label: "Basse température",
    temp: "35°C",
    desc: "Plancher chauffant — 35°C",
    max: 35,
    min: 25,
  },
  moyenne_haute_temp: {
    label: "Moyenne / Haute température",
    temp: "55 à 75°C",
    desc: "Radiateurs moyenne ou haute température",
    max: 75,
    min: 35,
  },
};

// Legacy emetteur mapping (for backward compatibility with old saved projects).
export const LEGACY_EMETTEURS = {
  plancher_chauffant: "basse_temp",
  radiateur_bt: "basse_temp",
  radiateur_ht: "moyenne_haute_temp",
};

// Département (FR code) -> climatic zone A-I.
export const DEPARTEMENT_TO_ZONE = {
  "06": "A", "13": "A", "83": "A", "2A": "A", "2B": "A",
  "11": "B", "17": "B", "22": "B", "29": "B", "33": "B", "35": "B",
  "40": "B", "44": "B", "50": "B", "56": "B", "64": "B", "66": "B", "85": "B",
  "09": "C", "16": "C", "24": "C", "30": "C", "31": "C", "32": "C", "34": "C",
  "46": "C", "47": "C", "65": "C", "79": "C", "81": "C", "82": "C", "84": "C",
  "14": "D", "27": "D", "28": "D", "36": "D", "37": "D", "41": "D", "45": "D",
  "49": "D", "53": "D", "60": "D", "61": "D", "72": "D", "75": "D", "76": "D",
  "77": "D", "78": "D", "86": "D", "91": "D", "92": "D", "93": "D", "94": "D",
  "95": "D",
  "03": "E", "12": "E", "15": "E", "18": "E", "19": "E", "23": "E", "42": "E",
  "43": "E", "48": "E", "63": "E", "87": "E",
  "02": "F", "59": "F", "62": "F", "80": "F",
  "01": "G", "04": "G", "05": "G", "07": "G", "10": "G", "21": "G", "26": "G",
  "38": "G", "39": "G", "51": "G", "55": "G", "58": "G", "69": "G", "71": "G",
  "73": "G", "74": "G", "89": "G",
  "25": "H", "52": "H", "54": "H", "70": "H", "88": "H", "90": "H",
  "08": "I", "57": "I", "67": "I", "68": "I",
};

export const DEPARTEMENT_NAMES = {
  "01": "Ain", "02": "Aisne", "03": "Allier", "04": "Alpes-de-Haute-Provence",
  "05": "Hautes-Alpes", "06": "Alpes-Maritimes", "07": "Ardèche", "08": "Ardennes",
  "09": "Ariège", "10": "Aube", "11": "Aude", "12": "Aveyron",
  "13": "Bouches-du-Rhône", "14": "Calvados", "15": "Cantal", "16": "Charente",
  "17": "Charente-Maritime", "18": "Cher", "19": "Corrèze", "2A": "Corse-du-Sud",
  "2B": "Haute-Corse", "21": "Côte-d'Or", "22": "Côtes-d'Armor", "23": "Creuse",
  "24": "Dordogne", "25": "Doubs", "26": "Drôme", "27": "Eure", "28": "Eure-et-Loir",
  "29": "Finistère", "30": "Gard", "31": "Haute-Garonne", "32": "Gers", "33": "Gironde",
  "34": "Hérault", "35": "Ille-et-Vilaine", "36": "Indre", "37": "Indre-et-Loire",
  "38": "Isère", "39": "Jura", "40": "Landes", "41": "Loir-et-Cher", "42": "Loire",
  "43": "Haute-Loire", "44": "Loire-Atlantique", "45": "Loiret", "46": "Lot",
  "47": "Lot-et-Garonne", "48": "Lozère", "49": "Maine-et-Loire", "50": "Manche",
  "51": "Marne", "52": "Haute-Marne", "53": "Mayenne", "54": "Meurthe-et-Moselle",
  "55": "Meuse", "56": "Morbihan", "57": "Moselle", "58": "Nièvre", "59": "Nord",
  "60": "Oise", "61": "Orne", "62": "Pas-de-Calais", "63": "Puy-de-Dôme",
  "64": "Pyrénées-Atlantiques", "65": "Hautes-Pyrénées", "66": "Pyrénées-Orientales",
  "67": "Bas-Rhin", "68": "Haut-Rhin", "69": "Rhône", "70": "Haute-Saône",
  "71": "Saône-et-Loire", "72": "Sarthe", "73": "Savoie", "74": "Haute-Savoie",
  "75": "Paris", "76": "Seine-Maritime", "77": "Seine-et-Marne", "78": "Yvelines",
  "79": "Deux-Sèvres", "80": "Somme", "81": "Tarn", "82": "Tarn-et-Garonne",
  "83": "Var", "84": "Vaucluse", "85": "Vendée", "86": "Vienne", "87": "Haute-Vienne",
  "88": "Vosges", "89": "Yonne", "90": "Territoire de Belfort", "91": "Essonne",
  "92": "Hauts-de-Seine", "93": "Seine-Saint-Denis", "94": "Val-de-Marne", "95": "Val-d'Oise",
};

export function normalizeDept(code) {
  if (!code) return null;
  const c = String(code).trim().toUpperCase();
  if (c === "2A" || c === "2B") return c;
  if (/^\d+$/.test(c)) return c.padStart(2, "0").slice(0, 2);
  return c;
}

export function zoneFromPostalCode(postalCode) {
  if (!postalCode) return null;
  const s = String(postalCode).trim();
  if (s.length < 2) return null;
  let dept = s.substring(0, 2);
  // Corsica: "20" prefix => 2A (200xx) or 2B (202xx-206xx)
  if (dept === "20") {
    const num = parseInt(s.substring(0, 5), 10);
    dept = num >= 20200 ? "2B" : "2A";
  }
  return DEPARTEMENT_TO_ZONE[dept] || null;
}

export function getBaseTemperature(zone, altitude) {
  if (!zone || !TEMPERATURE_TABLE[zone]) return null;
  let band = 200;
  for (const b of ALTITUDE_BANDS) {
    if (altitude <= b) {
      band = b;
      break;
    }
  }
  if (altitude > 1200) band = 1200;
  return TEMPERATURE_TABLE[zone][band];
}

export function computeDimensionnement(inputs) {
  const {
    surface,
    hauteur = 2.5,
    isolation,
    t_interieur = 19,
    zone,
    altitude,
    emetteur,
  } = inputs;

  // Map legacy keys to current ones.
  const emKey = LEGACY_EMETTEURS[emetteur] || emetteur;
  const isoKey = LEGACY_ISOLATION[isolation] || isolation;
  const g = ISOLATION_COEFFICIENTS[isoKey]?.value;
  const em = EMETTEURS[emKey];
  const t_base = getBaseTemperature(zone, altitude);

  if (!g || !em || t_base === null) return null;

  const volume = surface * hauteur;
  const delta_t = t_interieur - t_base;
  const deperditions_w = volume * g * delta_t;

  // Plage de dimensionnement : 60% à 130% des déperditions.
  const puissance_min = deperditions_w * 0.6;
  const puissance_max = deperditions_w * 1.3;
  const puissance_totale = deperditions_w * 1.2; // recommandée (20% marge)
  const ballon_tampon = (puissance_totale / 1000) * 15;
  const couverture_pct = deperditions_w > 0 ? (puissance_totale / deperditions_w) * 100 : 0;
  const alerte = couverture_pct > 130;

  return {
    volume_m3: round(volume, 2),
    coef_g: g,
    t_base,
    delta_t: round(delta_t, 2),
    deperditions_w: round(deperditions_w, 2),
    deperditions_kw: round(deperditions_w / 1000, 2),
    puissance_totale_w: round(puissance_totale, 2),
    puissance_totale_kw: round(puissance_totale / 1000, 2),
    puissance_min_w: round(puissance_min, 2),
    puissance_min_kw: round(puissance_min / 1000, 2),
    puissance_max_w: round(puissance_max, 2),
    puissance_max_kw: round(puissance_max / 1000, 2),
    couverture_pct: round(couverture_pct, 1),
    ballon_tampon_l: round(ballon_tampon, 1),
    surpuissance_pct: round(couverture_pct, 1),
    alerte_cycles_courts: alerte,
    emetteur_label: em.label,
    emetteur_temp: em.temp,
    emetteur_max: em.max,
    emetteur_min: em.min,
  };
}

function round(value, decimals) {
  const f = Math.pow(10, decimals);
  return Math.round(value * f) / f;
}
